import { styled } from "goober";
import { queries } from "../../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useMakeUrl, routes } from "../../../lib/urls";
import { CircleMesh } from "../../CircleMesh";
import { parseBadgeConfig } from "../../CircleMeshEditor";
import {
  BadgeWrapper,
  BranchControl,
  BranchLabel,
  LoadingContainer,
  StepColor,
  StepInfoContainer,
  StepList,
  StepNameRow,
  StepRow,
  WorkflowGroup,
  WorkflowHeader,
  WorkflowSection,
} from "../../ConnectorModal/usageList";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import { SpaceBadge } from "../../SpaceBadge";
import { Text } from "../../Text";
import { WavyLine } from "../../WavyLine";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// The primary provider also signs in every route that doesn't name one.
export function SsoUsageTab({
  providerId,
  isPrimary,
}: {
  providerId: string;
  isPrimary: boolean;
}) {
  const makeUrl = useMakeUrl();
  const [branches, details] = useQuery(
    queries.ssoProviderRouteDependencies({
      providerId,
      includeInherited: isPrimary,
    })
  );
  if (details.type !== "complete") {
    return (
      <LoadingContainer>
        <WavyLine />
      </LoadingContainer>
    );
  }
  const used = branches
    .filter((branch) => branch.workflow && branch.headCommit?.routes.length)
    .toSorted((a, b) => Number(b.name === "main") - Number(a.name === "main"));
  if (used.length === 0) {
    return (
      <EmptyState
        iconName="shield-check"
        title="Nothing is using this provider"
        description={
          isPrimary
            ? "Routes that sign in with this provider, or with tenant sign-in, show up here."
            : "Routes that sign in with this provider show up here."
        }
      />
    );
  }
  return (
    <Container>
      <WorkflowSection>
        {used.map((branch) => {
          const workflow = branch.workflow!;
          const live = branch.name === "main";
          const badge = parseBadgeConfig(workflow.badgeSeed, workflow.id);
          return (
            <WorkflowGroup key={branch.id}>
              <WorkflowHeader>
                <BadgeWrapper style={live ? undefined : { opacity: 0.5 }}>
                  <CircleMesh
                    targetCircleSize={8}
                    seed={badge.seed}
                    mutingIntensity={0}
                    animate={false}
                    restrictedColors={badge.colors}
                  />
                </BadgeWrapper>
                <Text variant="text-regular-md" ellipsis>
                  {workflow.name}
                </Text>
                {workflow.space && <SpaceBadge space={workflow.space} />}
                <BranchControl>
                  <BranchLabel>
                    <Icon
                      name="git-branch"
                      size={12}
                      color="var(--text-secondary)"
                    />
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {live ? "Live" : branch.name}
                    </Text>
                  </BranchLabel>
                </BranchControl>
              </WorkflowHeader>
              <StepList>
                {branch.headCommit!.routes.map((route) => (
                  <StepRow
                    key={route.route}
                    to={makeUrl(routes.space.workflow, {
                      spaceId: workflow.spaceId,
                      workflowId: workflow.id,
                      branchId: live ? "" : branch.id,
                      step: route.stepDir,
                    })}
                  >
                    <StepColor />
                    <StepInfoContainer>
                      <StepNameRow>
                        <Text variant="text-regular-md" ellipsis>
                          {route.stepDir}
                        </Text>
                      </StepNameRow>
                      <Text
                        variant="code-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {route.route}
                      </Text>
                    </StepInfoContainer>
                    {!route.routeIdp && (
                      <Text variant="text-small" color="var(--text-secondary)">
                        Tenant sign-in
                      </Text>
                    )}
                  </StepRow>
                ))}
              </StepList>
            </WorkflowGroup>
          );
        })}
      </WorkflowSection>
      <Text variant="text-small" color="var(--text-secondary)">
        Only workflows you can access are listed.
      </Text>
    </Container>
  );
}
